import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { reservationAPI, snapshotAPI, adminAPI, userAPI } from '../../api/client';
import { useToastStore } from '../../store/toastStore';
import SegmentControl from '../../components/SegmentControl';
import BusSeatLayout from '../../components/BusSeatLayout';
import BottomSheetSelect from '../../components/BottomSheetSelect';
import { isProtectedStudent } from '../../config/protectedAccount';

const EMPTY_FORM = {
  direction: 'DOWN',
  routeName: '',
  lineGroupSeq: '',
  busTime: '',
  busSeq: '',
  stopName: '',
  stopSeq: '',
};

export default function AdminReserve() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const adminId = user?.userId;
  const showToast = useToastStore((s) => s.showToast);
  const queryClient = useQueryClient();

  // ── 대상 사용자 ─────────────────────────────────────────────────────────────
  const [target, setTarget] = useState(() => {
    const id = searchParams.get('userId');
    return id
      ? { id, student_id: searchParams.get('studentId') || '', name: searchParams.get('name') || '' }
      : null;
  });
  const targetUserId = target?.id || '';

  // 사용자 검색
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = 미검색
  const [searching, setSearching] = useState(false);

  // ── 예약 플로우 상태 ────────────────────────────────────────────────────────
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [stops, setStops] = useState([]);
  const [selectedBus, setSelectedBus] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const resetFlow = () => {
    setFormData(EMPTY_FORM);
    setRoutes([]);
    setTimetable([]);
    setStops([]);
    setSelectedBus(null);
    setSelectedSeats([]);
    setError(null);
    setResult(null);
  };

  const selectTarget = (u) => {
    setTarget(u);
    setSearchParams({ userId: u.id, studentId: u.student_id || '', name: u.name || '' });
    setSearchResults(null);
    setSearchInput('');
    resetFlow();
  };

  const clearTarget = () => {
    setTarget(null);
    setSearchParams({});
    resetFlow();
  };

  const doSearch = async (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    if (!q) return;
    setSearching(true);
    try {
      const res = await adminAPI.getUsers({ page: 1, limit: 20, search: q });
      setSearchResults(res.users || res.data || []);
    } catch {
      showToast('사용자 검색에 실패했습니다', 'error');
    } finally {
      setSearching(false);
    }
  };

  // ── 대상 사용자 정보 (이름/잔액) ────────────────────────────────────────────
  const { data: targetInfo } = useQuery({
    queryKey: ['admin-reserve-userinfo', targetUserId],
    queryFn: async () => {
      const res = await userAPI.getInfo(targetUserId);
      return res.success ? res.data : null;
    },
    enabled: !!targetUserId,
    staleTime: 60_000,
    retry: 0,
  });

  // 보호 계정(소유자)은 대신 예약/취소 불가 — 백엔드도 403으로 차단
  const isProtected = isProtectedStudent(targetInfo?.studentId || target?.student_id);

  // ── 대상 사용자 현재 예약 (실시간, 20초 폴링) ───────────────────────────────
  const [cancelling, setCancelling] = useState(null); // `${busSeq}-${seatNo}`
  const {
    data: reservationsData,
    isLoading: loadingReservations,
    isFetching: fetchingReservations,
    refetch: refetchReservations,
  } = useQuery({
    queryKey: ['admin-reservations', targetUserId],
    queryFn: async () => {
      const res = await reservationAPI.getReservations(targetUserId);
      if (res.success) return res.data?.reservations || [];
      throw new Error(res.error || '예약 내역을 불러올 수 없습니다.');
    },
    enabled: !!targetUserId,
    refetchInterval: 20_000,
    retry: 1,
  });
  const reservations = reservationsData || [];

  const cancelMutation = useMutation({
    mutationFn: (reservation) => reservationAPI.cancel({ userId: targetUserId, reservation, actorAdminId: adminId }),
    onMutate: (reservation) => setCancelling(`${reservation.busSeq}-${reservation.seatNo}`),
    onSuccess: (res) => {
      if (res.success) {
        showToast('예약이 취소되었습니다.', 'success');
        queryClient.invalidateQueries({ queryKey: ['admin-reservations', targetUserId] });
      } else {
        showToast(res.error || '예약 취소에 실패했습니다.', 'error');
      }
    },
    onError: (err) => showToast(err.response?.data?.error || '예약 취소에 실패했습니다.', 'error'),
    onSettled: () => setCancelling(null),
  });

  const handleCancel = (r) => {
    if (isProtected) {
      showToast('보호된 계정의 예약은 대신 취소할 수 없습니다.', 'error');
      return;
    }
    if (!confirm(`${target?.student_id || '사용자'} · ${r.routeName} ${r.seatNo}번 좌석 예약을 취소할까요?`)) return;
    cancelMutation.mutate(r);
  };

  const formatDateTime = (str) => {
    if (!str) return '';
    try {
      return new Date(str).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch {
      return str;
    }
  };

  // ── 노선 스냅샷 (유저 무관, 전역) ───────────────────────────────────────────
  const { data: snapshotData, isLoading: loadingSnapshot } = useQuery({
    queryKey: ['snapshot'],
    queryFn: async () => {
      const res = await snapshotAPI.getLatest();
      if (res.success && res.data) return res.data;
      throw new Error('노선 정보를 불러올 수 없습니다.');
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const snapshot = snapshotData || null;

  // ── 좌석 조회 (1초 폴링, 대상 유저 토큰) ────────────────────────────────────
  const { data: seatsData, isLoading: loadingSeats } = useQuery({
    queryKey: ['admin-seats', targetUserId, formData.busSeq],
    queryFn: async () => {
      const res = await reservationAPI.getSeats(targetUserId, formData.busSeq);
      if (res.success) return res.data?.seats || [];
      throw new Error('좌석 정보를 불러올 수 없습니다.');
    },
    enabled: !!formData.busSeq && !!targetUserId,
    refetchInterval: 1000,
    placeholderData: (prev) => prev,
  });
  const seats = seatsData || [];

  // ── 노선 파생 ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!snapshot || !formData.direction) return;
    const routeData = snapshot.routes_data?.[formData.direction] || [];
    setRoutes(routeData.map((r) => ({ seq: r.lineGroupSeq, name: r.groupName, busCnt: r.busCnt })));
  }, [snapshot, formData.direction]);

  // ── 시간표/정류장 파생 + 실시간 업데이트 ────────────────────────────────────
  useEffect(() => {
    if (!snapshot || !formData.lineGroupSeq || !formData.direction || !targetUserId) return;

    const load = async () => {
      const routeData = snapshot.routes_data?.[formData.direction] || [];
      const sel = routeData.find((r) => r.lineGroupSeq === formData.lineGroupSeq);
      if (!sel) {
        setTimetable([]);
        setStops([]);
        return;
      }
      setTimetable(
        (sel.busList || []).map((b) => ({
          time: b.operateTime,
          remainingSeats: b.seatCount - b.appCount,
          readyCount: b.readyCount || 0,
          seq: b.busSeq,
          carNum: b.carNum,
        }))
      );
      setStops((sel.stopList || []).map((s) => ({ seq: s.seq, stopName: s.stopName })));

      try {
        const res = await reservationAPI.getTimetable(targetUserId, formData.direction, formData.lineGroupSeq);
        if (res.success && res.data?.timetable) setTimetable(res.data.timetable);
      } catch {
        /* 실시간 업데이트 실패 시 스냅샷 데이터 유지 */
      }
    };
    load();
  }, [snapshot, formData.lineGroupSeq, formData.direction, targetUserId]);

  // ── 예약 실행 ───────────────────────────────────────────────────────────────
  const reserveMutation = useMutation({
    mutationFn: (data) => reservationAPI.execute(data),
    onMutate: () => {
      setError(null);
      setResult(null);
    },
    onSuccess: (res) => {
      setResult(res);
      if (res.success) {
        showToast(`${target?.student_id || '사용자'} 예약 완료!`, 'success');
        setSelectedSeats([]);
        queryClient.invalidateQueries({ queryKey: ['admin-reservations', targetUserId] });
      } else {
        setError(res.error || '예약에 실패했습니다.');
      }
    },
    onError: (err) => {
      const m = err.response?.data?.error || '예약에 실패했습니다.';
      setError(m);
      setResult({ success: false, error: m });
    },
  });

  // ── 핸들러 ──────────────────────────────────────────────────────────────────
  const handleDirectionChange = (val) => {
    setFormData({ ...EMPTY_FORM, direction: val });
    setRoutes([]);
    setTimetable([]);
    setStops([]);
    setSelectedBus(null);
    setSelectedSeats([]);
  };

  const handleRouteChange = (e) => {
    const sel = routes.find((r) => r.seq === e.target.value);
    if (!sel) return;
    setFormData({ ...formData, lineGroupSeq: sel.seq, routeName: sel.name, busTime: '', busSeq: '', stopName: '', stopSeq: '' });
    setTimetable([]);
    setStops([]);
    setSelectedBus(null);
    setSelectedSeats([]);
  };

  const handleBusChange = (bus) => {
    setSelectedBus(bus);
    setFormData({ ...formData, busTime: bus.time, busSeq: bus.seq });
    setSelectedSeats([]);
  };

  const handleStopChange = (e) => {
    const sel = stops.find((s) => s.seq.toString() === e.target.value);
    if (sel) setFormData({ ...formData, stopSeq: sel.seq.toString(), stopName: sel.stopName });
  };

  const handleSeatClick = (seat) => {
    if (!seat.available) return;
    setSelectedSeats(selectedSeats.includes(seat.number) ? [] : [seat.number]);
  };

  const handleSubmit = () => {
    if (!targetUserId) {
      showToast('대상 사용자를 먼저 선택하세요', 'error');
      return;
    }
    if (isProtected) {
      showToast('보호된 계정으로는 대신 예약할 수 없습니다.', 'error');
      return;
    }
    if (!formData.stopSeq) {
      showToast('정류장을 선택해주세요', 'error');
      return;
    }
    if (selectedSeats.length === 0) {
      showToast('좌석을 선택해주세요', 'error');
      return;
    }
    reserveMutation.mutate({
      userId: targetUserId,
      busSeq: formData.busSeq,
      seatNo: selectedSeats[0],
      stopSeq: parseInt(formData.stopSeq),
      actorAdminId: adminId,
    });
  };

  // ── 렌더 ────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold text-charcoal">대신 예약</h2>
        <p className="text-sm text-sub-text mt-1">사용자를 선택해 해당 사용자 명의로 버스를 예약합니다</p>
      </div>

      {/* 대상 사용자 선택 */}
      {!target ? (
        <div className="bg-white rounded-2xl shadow-card p-5">
          <h3 className="text-base font-bold text-charcoal mb-3">대상 사용자</h3>
          <form onSubmit={doSearch} className="flex gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="학번 또는 이름으로 검색"
              className="flex-1 px-4 py-2 rounded-xl border border-border text-sm text-charcoal placeholder:text-sub-text focus:outline-none focus:ring-2 focus:ring-tayo/30 bg-white"
            />
            <button
              type="submit"
              disabled={searching}
              className="px-5 py-2 rounded-xl bg-tayo text-white text-sm font-medium hover:bg-tayo-dark transition-colors disabled:opacity-50"
            >
              {searching ? '검색 중...' : '검색'}
            </button>
          </form>

          {searchResults !== null && (
            <div className="mt-3 divide-y divide-border border border-border rounded-xl overflow-hidden">
              {searchResults.length === 0 ? (
                <p className="text-sm text-sub-text text-center py-6">검색 결과가 없습니다</p>
              ) : (
                searchResults.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => selectTarget(u)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-snow transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-charcoal">{u.student_id || '-'}</p>
                      <p className="text-xs text-sub-text">{u.name || '이름 없음'}</p>
                    </div>
                    <span className="text-xs text-tayo font-medium">선택 →</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-tayo-light/60 border border-tayo/20 rounded-2xl p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-tayo font-semibold mb-0.5">대상 사용자</p>
            <p className="text-base font-bold text-charcoal">
              {targetInfo?.studentId || target.student_id || '-'}
              {(targetInfo?.name || target.name) && (
                <span className="ml-2 text-sm font-medium text-sub-text">{targetInfo?.name || target.name}</span>
              )}
            </p>
            {targetInfo?.remainCash != null && (
              <p className="text-xs text-sub-text mt-0.5">잔액 {String(targetInfo.remainCash).trim()}P</p>
            )}
          </div>
          <button
            onClick={clearTarget}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-tayo/30 text-tayo bg-white hover:bg-tayo-light transition-colors"
          >
            변경
          </button>
        </div>
      )}

      {/* 보호 계정 안내 */}
      {target && isProtected && (
        <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4 flex items-start gap-3">
          <span className="text-lg leading-none">🔒</span>
          <div>
            <p className="text-sm font-bold text-violet-700">보호된 계정입니다</p>
            <p className="text-xs text-violet-500 mt-0.5">
              이 계정은 관리자가 대신 예약하거나 대신 취소할 수 없습니다.
            </p>
          </div>
        </div>
      )}

      {/* 현재 예약 (실시간) + 대신 취소 */}
      {target && (
        <div className="bg-white rounded-2xl shadow-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-charcoal">
              현재 예약
              {reservations.length > 0 && (
                <span className="ml-2 text-sm font-medium text-sub-text">{reservations.length}건</span>
              )}
            </h3>
            <button
              onClick={() => refetchReservations()}
              disabled={fetchingReservations}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-sub-text hover:bg-snow transition-colors disabled:opacity-50"
            >
              <svg
                className={`w-3.5 h-3.5 ${fetchingReservations ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M3.985 14.652H-.008m.015 0h4.992m-4.992 0v4.992m0-4.992 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              새로고침
            </button>
          </div>

          {loadingReservations ? (
            <div className="flex justify-center py-8">
              <div className="w-7 h-7 border-2 border-tayo/20 border-t-tayo rounded-full animate-spin" />
            </div>
          ) : reservations.length === 0 ? (
            <p className="text-sub-text text-center py-6 text-sm">예약된 버스가 없습니다</p>
          ) : (
            <div className="space-y-3">
              {reservations.map((r, i) => (
                <div key={`${r.busSeq}-${r.seatNo}-${i}`} className="border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        r.direction === 'UP' ? 'bg-tayo-light text-tayo' : 'bg-orange-50 text-orange-600'
                      }`}
                    >
                      {r.direction === 'UP' ? '등교' : '하교'}
                    </span>
                    <span className="text-sm font-bold text-charcoal">{r.routeName}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-sub-text">
                    <span>🕒 {formatDateTime(r.operateTime)} 출발</span>
                    <span>💺 {r.seatNo}번 좌석</span>
                    <span>📍 {r.stopName}</span>
                    {r.carNum && <span>🚌 {r.carNum}호</span>}
                  </div>
                  {isProtected ? (
                    <p className="mt-2 text-xs text-violet-400">🔒 보호 계정 — 대신 취소 불가</p>
                  ) : r.canCancel ? (
                    <button
                      onClick={() => handleCancel(r)}
                      disabled={cancelling === `${r.busSeq}-${r.seatNo}`}
                      className="mt-3 w-full py-2 text-sm font-semibold rounded-lg text-coral bg-coral/10 hover:bg-coral/20 transition-colors disabled:opacity-50"
                    >
                      {cancelling === `${r.busSeq}-${r.seatNo}` ? '취소 중...' : '대신 취소'}
                    </button>
                  ) : (
                    <p className="mt-2 text-xs text-sub-text">취소 불가 (출발 시간 경과 또는 탑승 완료)</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 예약 플로우 */}
      {target && !isProtected && (
        <>
          {error && (
            <div className="bg-coral/10 border border-coral/20 rounded-xl p-4">
              <p className="text-sm text-coral font-medium">{error}</p>
            </div>
          )}
          {result?.success && (
            <div className="bg-mint/10 border border-mint/20 rounded-xl p-4">
              <p className="text-sm text-mint font-medium">예약이 완료되었습니다.</p>
            </div>
          )}

          {loadingSnapshot && !snapshot && (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-tayo/20 border-t-tayo rounded-full animate-spin" />
            </div>
          )}

          {/* 방향 / 노선 / 정류장 */}
          {snapshot && (
            <div className="bg-white rounded-2xl shadow-card p-5">
              <h3 className="text-lg font-bold text-charcoal mb-4">어디로 가나요?</h3>
              <SegmentControl
                options={[
                  { value: 'UP', label: '등교' },
                  { value: 'DOWN', label: '하교' },
                ]}
                value={formData.direction}
                onChange={handleDirectionChange}
              />

              {routes.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-charcoal mb-2">노선</label>
                  <BottomSheetSelect
                    value={formData.lineGroupSeq}
                    onChange={handleRouteChange}
                    options={routes.map((r) => ({ value: r.seq, label: r.name, sublabel: `${r.busCnt}대` }))}
                    placeholder="노선을 선택하세요"
                    title="노선 선택"
                    searchable={routes.length > 5}
                  />
                </div>
              )}

              {stops.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-charcoal mb-2">정류장</label>
                  <BottomSheetSelect
                    value={formData.stopSeq}
                    onChange={handleStopChange}
                    options={stops.map((s) => ({ value: s.seq, label: s.stopName }))}
                    placeholder="정류장을 선택하세요"
                    title="정류장 선택"
                    searchable={stops.length > 5}
                  />
                </div>
              )}
            </div>
          )}

          {/* 시간 */}
          {formData.lineGroupSeq && (
            <div className="bg-white rounded-2xl shadow-card p-5">
              <h3 className="text-lg font-bold text-charcoal mb-4">언제 타나요?</h3>
              {timetable.length === 0 ? (
                <p className="text-sub-text text-sm">시간표를 불러오는 중...</p>
              ) : (
                <BottomSheetSelect
                  value={formData.busSeq}
                  onChange={(e) => {
                    const bus = timetable.find((b) => String(b.seq) === e.target.value);
                    if (bus) handleBusChange(bus);
                  }}
                  options={timetable.map((bus) => {
                    const s = bus.remainingSeats;
                    const w = bus.readyCount || 0;
                    const sublabelColor = s === 0 ? 'text-sub-text' : s <= 10 ? 'text-coral' : s <= 22 ? 'text-amber-500' : 'text-mint';
                    return {
                      value: bus.seq,
                      label: bus.time,
                      sublabel: s > 0 ? `${s}석` : w > 0 ? `만석 · 대기 ${w}명` : '만석',
                      sublabelColor,
                    };
                  })}
                  placeholder="시간을 선택하세요"
                  title="시간 선택"
                  searchable={timetable.length > 10}
                />
              )}
            </div>
          )}

          {/* 좌석 */}
          {formData.busSeq && (
            <div className="bg-white rounded-2xl shadow-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-charcoal">어디 앉나요?</h3>
                {selectedBus && (
                  <span className="text-xs text-sub-text bg-gray-100 px-2.5 py-1 rounded-full">{selectedBus.time}</span>
                )}
              </div>
              {loadingSeats ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-2 border-tayo/20 border-t-tayo rounded-full animate-spin" />
                </div>
              ) : seats.length === 0 ? (
                <p className="text-sub-text text-center py-4">좌석 정보가 없습니다</p>
              ) : (
                <BusSeatLayout
                  seats={seats}
                  selectedSeats={selectedSeats}
                  onSeatClick={(seatNum) => {
                    const seat = seats.find((s) => s.number === seatNum);
                    if (seat) handleSeatClick(seat);
                  }}
                  mode="reserve"
                  totalSeats={seats.length}
                />
              )}
            </div>
          )}

          {/* 예약 버튼 */}
          {selectedSeats.length > 0 && formData.stopSeq && (
            <button
              onClick={handleSubmit}
              disabled={reserveMutation.isPending}
              className="w-full py-4 bg-gradient-to-r from-tayo to-tayo-dark text-white rounded-xl font-bold text-base disabled:opacity-50 shadow-lg transition-all"
            >
              {reserveMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  예약 중...
                </span>
              ) : (
                `${target.student_id || '사용자'} · ${selectedSeats[0]}번 좌석 대신 예약하기`
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}
